import type { BloqueioEdicaoProjecao, PlanejamentoControle } from "@/modules/financeiro-obras";
import type { CotacaoDaObra, ModoProjecao, ProjecaoDialogProps } from "./projecao-dialog";
import type { ObraDaProjecao } from "./projecao-formulario";
import {
  MOTIVO_EDITAR_CANCELADO,
  MOTIVO_CANCELAMENTO_PENDENTE,
  MOTIVO_EDITAR_EM_FECHAMENTO,
  MOTIVO_EDITAR_SALDO_A_DESTINAR,
  MOTIVO_REVISAO_PENDENTE,
  MOTIVO_SEM_PERMISSAO_EDITAR,
} from "./planejamento-controle.constants";
import { localizarEscopo } from "./planejamento-controle.cancelamento";
import { formatCompetencia, formatMoeda } from "./planejamento-controle.format";

const dataBrasileira = (iso: string) => iso.slice(0, 10).split("-").reverse().join("/");

const MOTIVO_POR_BLOQUEIO: Record<BloqueioEdicaoProjecao, string> = {
  REVISAO_PENDENTE: MOTIVO_REVISAO_PENDENTE,
  SOLICITACAO_PENDENTE: MOTIVO_CANCELAMENTO_PENDENTE,
  EM_FECHAMENTO: MOTIVO_EDITAR_EM_FECHAMENTO,
  CANCELADO: MOTIVO_EDITAR_CANCELADO,
  SALDO_A_DESTINAR: MOTIVO_EDITAR_SALDO_A_DESTINAR,
};

export function motivoEdicaoBloqueada(
  planejamento: PlanejamentoControle,
  obraEscopoMacroId: string,
  podeProjetar: boolean,
): string | null {
  const escopo = localizarEscopo(planejamento, obraEscopoMacroId)?.escopo;
  if (!escopo) return null;
  if (!podeProjetar) return MOTIVO_SEM_PERMISSAO_EDITAR;
  const { modo, bloqueio } = escopo.edicaoProjecao;
  if (bloqueio) return MOTIVO_POR_BLOQUEIO[bloqueio];
  return modo === null ? MOTIVO_REVISAO_PENDENTE : null;
}

export function modoDaProjecao(
  planejamento: PlanejamentoControle,
  obraEscopoMacroId: string,
): ModoProjecao {
  const modo = localizarEscopo(planejamento, obraEscopoMacroId)?.escopo.edicaoProjecao.modo;
  return modo === "EDICAO" ? "edicao" : "inicial";
}

export function montarObraDaProjecao(planejamento: PlanejamentoControle): {
  obra: ObraDaProjecao;
  cotacaoDaObra: CotacaoDaObra | null;
} {
  const { obra, cotacao } = planejamento;
  return {
    obra: {
      embarcacao: obra.embarcacao?.nome ?? "—",
      idadeEmbarcacao: obra.embarcacao?.idade ?? null,
      dataInicio: obra.dataInicio ? dataBrasileira(obra.dataInicio) : "—",
      inicioMensalizacao: formatCompetencia(obra.dataInicio),
    },
    cotacaoDaObra: cotacao
      ? { moeda: cotacao.moeda, valor: formatMoeda(cotacao.valor, "BRL").replace("R$ ", "") }
      : null,
  };
}

type DadosDaProjecao = Pick<ProjecaoDialogProps, "escopo" | "obra" | "cotacaoDaObra">;

export function montarProjecao(
  planejamento: PlanejamentoControle,
  obraEscopoMacroId: string,
): DadosDaProjecao | null {
  const achado = localizarEscopo(planejamento, obraEscopoMacroId);
  if (!achado) return null;
  const { centro, tipo, escopo } = achado;

  return {
    escopo: {
      obraEscopoMacroId,
      obraTipoObraId: tipo.id,
      nome: escopo.nome,
      tipoObra: `${tipo.tipoObra.codigo} · ${tipo.tipoObra.nome}`,
      centroCusto: `${centro.codigo} · ${centro.nome}`,
      origem: escopo.origem?.nome ?? "—",
      classificacao: escopo.classificacao,
    },
    ...montarObraDaProjecao(planejamento),
  };
}
